import { useCatalogueContext } from '../CatalogueContext';
import { useState } from 'react';
import SelectTypes from '../SelectTypes';
import { config } from '../../../globals/config';
import CustomButton from '../../Custom/Button';
import Grid from '@mui/material/Grid';
import WmsCards from './WmsCards';
import { Button, Paper, ButtonBase, Typography, Box } from '@mui/material';
import {
  ZoomInIcon,
  DeleteIcon,
  DoNotDisturbOnOutlinedIcon,
  UnfoldMoreOutlinedIcon,
  UnfoldLessOutlinedIcon,
} from '../../../globals/icons';

interface DisplayCardsProps {
  viewerRef: any;
  layersDisplayed: any[];
  polygonsDisplayed: any[];
  setPolygonsDisplayed: (polygons: any[]) => void;
  zoomToPolygon: (polygon: any) => void;
}

const DisplayCards: React.FC<DisplayCardsProps> = ({
  viewerRef,
  layersDisplayed,
  polygonsDisplayed,
  setPolygonsDisplayed,
  zoomToPolygon,
}) => {
  if (layersDisplayed.length === 0 && polygonsDisplayed.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          height: '100%',
          textAlign: 'center',
          padding: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'gray',
          }}
        >
          Your workbench is empty
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'gray',
          }}
        >
          <Typography variant="caption">
            Browse available data by selecting "Search the Catalogue". Once
            you’ve added data to the map, your active data sets are listed
            here in your workbench. The workbench will help you to interact
            with the data. In the workbench, you can toggle data sets on and
            off, change their opacity, activate the split-screen comparison,
            change styles, and navigate through dates and times if the data
            supports this functionality.
          </Typography>
        </Box>
      </Box>
    );
  }

  const onRemovePolygon = (polygon: any) => {
    const { cesiumElement } = viewerRef.current;
    cesiumElement.entities.remove(polygon);
    setPolygonsDisplayed(
      polygonsDisplayed.filter((x) => x.id !== polygon.id),
    );
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      {polygonsDisplayed.map((x, i) => (
        <Box key={x.id} sx={{ mb: 2 }}>
          <Paper elevation={3} sx={{ padding: 2 }}>
            <Box fontSize={16} fontWeight="bold" mb={1}>
              {x.id}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() => zoomToPolygon(x)}
                  endIcon={<ZoomInIcon />}
                >
                  Ideal Zoom
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  onClick={() => onRemovePolygon(x)}
                  endIcon={<DeleteIcon />}
                >
                  Remove
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      ))}
      <WmsCards items={layersDisplayed} />
    </Box>
  );
}


export default function SideBar() {
  const {
    setIsModalProducts,
    setCatalogueModalOpen,
    isMapLoading,
    viewerRef,
    layersDisplayed,
    polygonsDisplayed,
    setPolygonsDisplayed,
    setLayersDisplayed,
    updatePayloadStatus,
  } = useCatalogueContext();
  const [areAllCollapsed, setAllCollapsed] = useState(true);

  const zoomToPolygon = (polygon: any) => {
    const { cesiumElement } = viewerRef.current;
    const { rectangle } = polygon;
    cesiumElement.camera.flyTo({
      destination: rectangle,
      duration: 2.0,
    });
    return;
  };


  const onRemoveAll = async () => {
    await viewerRef;
    const { cesiumElement } = viewerRef.current;
    console.log(cesiumElement.entities.removeAll());
    setPolygonsDisplayed([]);
    setLayersDisplayed([]);
  };

  const onChangeCollapse = () => {
    setAllCollapsed(!areAllCollapsed);
    for (let layerDisplayer of layersDisplayed) {
      setLayersDisplayed((prevLayers) => {
        const updatedLayers = prevLayers.map((layer) => {
          if (layer.id === layerDisplayer.id) {
            return { ...layer, isExpanded: !layer.isExpanded };
          }
          return layer;
        });
        return updatedLayers;
      });
    }
  };

  const clickableSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    cursor: 'pointer',
    px: 1,
    py: 0.5,
    borderRadius: 1,
    '&:hover': {
      bgcolor: 'action.hover',
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh', // or set a fixed height / maxHeight as needed
      }}
    >
      <img
        src={config.companyLogo}
        alt="Logo"
        style={{ width: '100%', height: 'auto' }}
      />
      <Box sx={{ px: 2, pt: 2 }}>
        <CustomButton
          onClick={() => {
            setIsModalProducts(true);
            setCatalogueModalOpen(true);
            updatePayloadStatus('all_status');
          }}
          fullWidth
          variant="contained"
        >
          Search the catalogue
        </CustomButton>
      </Box>
      <Grid container sx={{ textAlign: 'center', fontSize: 12, px: 2, pt: 2 }}>
        <Grid
          item
          xs={12}
          sm={12}
          md={4}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="body2"
            component="span"
            sx={{ userSelect: 'none' }}
          >
            DATA SETS ({layersDisplayed.length + polygonsDisplayed.length})
          </Typography>
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          md={4}
          sx={{ display: 'flex', justifyContent: 'center' }}
        >
          <ButtonBase
            sx={clickableSx}
            onClick={onRemoveAll}
            aria-label="Remove all datasets"
            focusRipple
          >
            <DoNotDisturbOnOutlinedIcon fontSize="small" />
            <Typography variant="body2" component="span">
              Remove all
            </Typography>
          </ButtonBase>
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          md={4}
          sx={{ display: 'flex', justifyContent: 'center' }}
        >
          <ButtonBase
            sx={clickableSx}
            onClick={onChangeCollapse}
            aria-label={
              areAllCollapsed
                ? 'Uncollapse all datasets'
                : 'Collapse all datasets'
            }
            focusRipple
          >
            {areAllCollapsed ? (
              <>
                <UnfoldMoreOutlinedIcon fontSize="small" />
                <Typography variant="body2" component="span">
                  Uncollapse
                </Typography>
              </>
            ) : (
              <>
                <UnfoldLessOutlinedIcon fontSize="small" />
                <Typography variant="body2" component="span">
                  Collapse
                </Typography>
              </>
            )}
          </ButtonBase>
        </Grid>
      </Grid>
      <Box
        sx={{
          p: 2,
        }}
      >
        <SelectTypes />
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          px: 2,
          pb: 2,
          display: 'flex',
          alignItems: 'start',
        }}
      >
        <DisplayCards
          viewerRef={viewerRef}
          layersDisplayed={layersDisplayed}
          polygonsDisplayed={polygonsDisplayed}
          setPolygonsDisplayed={setPolygonsDisplayed}
          zoomToPolygon={zoomToPolygon}
        />
      </Box>
    </Box>
  );
}
